import type { TFunction } from "i18next";

/**
 * Notification rows come from the DB pre-rendered in English (title, message,
 * action_text). This helper translates them on the client by looking up
 * `n.type` and substituting the parcel/booking reference and any other
 * dynamic values extracted from `n.data` or the message itself.
 *
 * When a type is unknown, we fall back to the original strings so we never
 * blank out a notification.
 */

export interface NotificationLike {
  type: string;
  title?: string | null;
  message?: string | null;
  action_text?: string | null;
  priority?: string | null;
  category?: string | null;
  data?: Record<string, any> | null;
}

const REF_RE = /(EC-\d{4}-\d{6}|BK\d{8,}|EC\d{8,})/i;
const AMOUNT_RE = /(\d+(?:[.,]\d+)?)\s*(MAD|EUR|USD|GBP|دم|€|\$|£)/i;

function pickRef(n: NotificationLike): string {
  return (
    (n.data && (n.data.ref || n.data.booking_ref || n.data.shipment_ref || n.data.tracking_number)) ||
    n.message?.match(REF_RE)?.[1] ||
    ""
  );
}

function pickAmount(n: NotificationLike): { amount: string; currency: string } {
  const m = n.message?.match(AMOUNT_RE);
  return {
    amount: (n.data && (n.data.amount || n.data.offer_amount)) || m?.[1] || "",
    currency: (n.data && (n.data.currency)) || m?.[2] || "",
  };
}

/** Map notification.type → translation keys for title/message. */
const TYPE_KEYS: Record<string, { titleKey: string; messageKey: string; actionKey?: string }> = {
  booking_assigned:       { titleKey: "notif.booking_assigned.title",       messageKey: "notif.booking_assigned.message",       actionKey: "notif.action.viewBooking" },
  booking_created:        { titleKey: "notif.booking_created.title",        messageKey: "notif.booking_created.message",        actionKey: "notif.action.viewBooking" },
  booking_confirmed:      { titleKey: "notif.booking_confirmed.title",      messageKey: "notif.booking_confirmed.message",      actionKey: "notif.action.viewBooking" },
  booking_cancelled:      { titleKey: "notif.booking_cancelled.title",      messageKey: "notif.booking_cancelled.message",      actionKey: "notif.action.viewBooking" },
  booking_completed:      { titleKey: "notif.booking_completed.title",      messageKey: "notif.booking_completed.message",      actionKey: "notif.action.viewBooking" },
  shipment_transit:       { titleKey: "notif.shipment_transit.title",       messageKey: "notif.shipment_transit.message",       actionKey: "notif.action.viewBooking" },
  shipment_collected:     { titleKey: "notif.shipment_collected.title",     messageKey: "notif.shipment_collected.message",     actionKey: "notif.action.viewBooking" },
  shipment_delivered:     { titleKey: "notif.shipment_delivered.title",     messageKey: "notif.shipment_delivered.message",     actionKey: "notif.action.viewBooking" },
  parcel_collected:       { titleKey: "notif.shipment_collected.title",     messageKey: "notif.shipment_collected.message",     actionKey: "notif.action.viewBooking" },
  parcel_in_transit:      { titleKey: "notif.shipment_transit.title",       messageKey: "notif.shipment_transit.message",       actionKey: "notif.action.viewBooking" },
  parcel_delivered:       { titleKey: "notif.shipment_delivered.title",     messageKey: "notif.shipment_delivered.message",     actionKey: "notif.action.viewBooking" },
  new_bid:                { titleKey: "notif.new_bid.title",                messageKey: "notif.new_bid.message",                actionKey: "notif.action.viewJob" },
  bid_accepted:           { titleKey: "notif.bid_accepted.title",           messageKey: "notif.bid_accepted.message",           actionKey: "notif.action.viewJob" },
  bid_declined:           { titleKey: "notif.bid_declined.title",           messageKey: "notif.bid_declined.message",           actionKey: "notif.action.viewJob" },
  match_offer_new:        { titleKey: "notif.match_offer_new.title",        messageKey: "notif.match_offer_new.message",        actionKey: "notif.action.viewOffer" },
  match_offer_accepted:   { titleKey: "notif.match_offer_accepted.title",   messageKey: "notif.match_offer_accepted.message",   actionKey: "notif.action.viewOffer" },
  match_offer_removed:    { titleKey: "notif.match_offer_removed.title",    messageKey: "notif.match_offer_removed.message" },
  match_offer_expired:    { titleKey: "notif.match_offer_expired.title",    messageKey: "notif.match_offer_expired.message" },
  question_received:      { titleKey: "notif.question_received.title",      messageKey: "notif.question_received.message",      actionKey: "notif.action.viewMessage" },
  answer_received:        { titleKey: "notif.answer_received.title",        messageKey: "notif.answer_received.message",        actionKey: "notif.action.viewMessage" },
  payment_received:       { titleKey: "notif.payment_received.title",       messageKey: "notif.payment_received.message",       actionKey: "notif.action.viewPayment" },
  payment_failed:         { titleKey: "notif.payment_failed.title",         messageKey: "notif.payment_failed.message",         actionKey: "notif.action.viewPayment" },
  dispute_opened:         { titleKey: "notif.dispute_opened.title",         messageKey: "notif.dispute_opened.message",         actionKey: "notif.action.viewDispute" },
  dispute_resolved:       { titleKey: "notif.dispute_resolved.title",       messageKey: "notif.dispute_resolved.message",       actionKey: "notif.action.viewDispute" },
  trip_created:           { titleKey: "notif.trip_created.title",           messageKey: "notif.trip_created.message",           actionKey: "notif.action.viewTrip" },
  trip_updated:           { titleKey: "notif.trip_updated.title",           messageKey: "notif.trip_updated.message",           actionKey: "notif.action.viewTrip" },
  trip_completed:         { titleKey: "notif.trip_completed.title",         messageKey: "notif.trip_completed.message",         actionKey: "notif.action.viewTrip" },
  system_announcement:    { titleKey: "notif.system_announcement.title",    messageKey: "notif.system_announcement.message" },
  // Additional real-world DB types
  delivery_complete:      { titleKey: "notif.delivery_complete.title",      messageKey: "notif.delivery_complete.message",      actionKey: "notif.action.viewBooking" },
  bid_expired:            { titleKey: "notif.bid_expired.title",            messageKey: "notif.bid_expired.message",            actionKey: "notif.action.browseJobs" },
  new_message:            { titleKey: "notif.new_message.title",            messageKey: "notif.new_message.message",            actionKey: "notif.action.reply" },
  support_reply:          { titleKey: "notif.support_reply.title",          messageKey: "notif.support_reply.message",          actionKey: "notif.action.viewTicket" },
  review_received:        { titleKey: "notif.review_received.title",        messageKey: "notif.review_received.message",        actionKey: "notif.action.viewReview" },
  staff_new_ticket:       { titleKey: "notif.staff_new_ticket.title",       messageKey: "notif.staff_new_ticket.message",       actionKey: "notif.action.viewTicket" },
  staff_new_livechat:     { titleKey: "notif.staff_new_livechat.title",     messageKey: "notif.staff_new_livechat.message",     actionKey: "notif.action.openChat" },
  doc_approved:           { titleKey: "notif.doc_approved.title",           messageKey: "notif.doc_approved.message",           actionKey: "notif.action.viewStatus" },
  doc_rejected:           { titleKey: "notif.doc_rejected.title",           messageKey: "notif.doc_rejected.message",           actionKey: "notif.action.viewStatus" },
  doc_expired:            { titleKey: "notif.doc_expired.title",            messageKey: "notif.doc_expired.message",            actionKey: "notif.action.viewStatus" },
};

/**
 * Maps the English action_text strings stored in the DB to translation keys
 * under `notif.action.*`. Used as a fallback when the notification type isn't
 * mapped in TYPE_KEYS or when action_text comes from an older row.
 */
const ACTION_TEXT_LOOKUP: Record<string, string> = {
  "View Booking": "notif.action.viewBooking",
  "View Job": "notif.action.viewJob",
  "View Offer": "notif.action.viewOffer",
  "View Payment": "notif.action.viewPayment",
  "View Trip": "notif.action.viewTrip",
  "View Message": "notif.action.viewMessage",
  "View Dispute": "notif.action.viewDispute",
  "Open Chat": "notif.action.openChat",
  "Reply": "notif.action.reply",
  "View Ticket": "notif.action.viewTicket",
  "View Status": "notif.action.viewStatus",
  "View Review": "notif.action.viewReview",
  "View Details": "notif.action.viewDetails",
  "Browse Jobs": "notif.action.browseJobs",
  "View Profile": "notif.action.viewProfile",
};

export function localizeNotification(n: NotificationLike, t: TFunction) {
  const map = TYPE_KEYS[n.type];
  const ref = pickRef(n);
  const { amount, currency } = pickAmount(n);

  const title = map
    ? t(map.titleKey, { defaultValue: n.title || "", ref, amount, currency })
    : (n.title || "");
  const message = map
    ? t(map.messageKey, { defaultValue: n.message || "", ref, amount, currency })
    : (n.message || "");
  let actionText: string | undefined;
  if (n.action_text) {
    if (map?.actionKey) {
      actionText = t(map.actionKey, { defaultValue: n.action_text });
    } else {
      const lookup = ACTION_TEXT_LOOKUP[n.action_text.trim()];
      actionText = lookup ? t(lookup, { defaultValue: n.action_text }) : n.action_text;
    }
  }

  const priorityLabel = n.priority
    ? t(`notif.priority.${n.priority}`, { defaultValue: n.priority })
    : "";
  const categoryLabel = n.category
    ? t(`notif.category.${n.category}`, { defaultValue: n.category })
    : "";
  const typeLabel = t(`notif.type.${n.type}`, { defaultValue: n.type });

  return { title, message, actionText, priorityLabel, categoryLabel, typeLabel };
}